import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    FIELD_LABELS,
    ORDER_SALE_FIELD_LABELS,
    formatDate,
    formatFloat,
    shortLabel,
    useBoolean,
    useNumber,
} from '../../../../../../helpers';
import {
  ColumnOrder,
  OrderSalesPageQuery,
  OrderSalesSortableFields,
  RoleId,
  useCurrentUserQuery,
  useOrderSalesPageLazyQuery
} from '../../../../../../services';
import {
  CCardList,
  CMinifiedTable,
  DateRange,
  DBlock,
  DCheckbox,
  DDateRangePicker,
  DIdAutocomplete,
  ProductScope,
  selectPaginated,
  useCardListSort,
  usePaginatedListBlock
} from '../../../../../dum';
import {
  SDeleteOrderSaleDialog,
  SUpdateOrderSaleDetailsDialog,
  SUpdateOrderSaleStatusDialog,
  SUpsertOrderSale
} from '../../../../../smart';
import { TOrderSale } from '../order-sales-page';
import { SaleCard } from './sale-card';

type SaleDoc = OrderSalesPageQuery['paginatedOrderSales']['docs'][number];

interface ISalesListBlock {
    setSelectedOrderSale?: (orderSale: TOrderSale) => void;
    // When set, embeds the block on a product's page (all sales of that
    // product). Undefined renders the full standalone Ventas page.
    scope?: ProductScope;
    // Isolates filter/paginator localStorage state. Pass a unique key when
    // embedding (e.g. on the product page) so state doesn't collide with the
    // standalone Ventas page.
    storageKey?: string;
}

export function SalesListBlock({
    setSelectedOrderSale,
    scope,
    storageKey,
}: ISalesListBlock = {}) {
    const KEY = storageKey ?? SalesListBlock.name;
    const embedded = !!scope;
    const productId = scope?.id ?? null;

    const [query, { data, previousData, loading }] =
        useOrderSalesPageLazyQuery();
    const [{ open, disabled }, setUpsertDialogOptions] = useState({
        open: false,
        disabled: false,
    });
    const [statusOpen, setStatusOpen] = useState(false);
    const [statusId, setStatusId] = useState<null | number>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    // Status changes are admin-only (Super / General), matching the backend
    // updateOrderSaleStatus gate. Non-admins never see the context item.
    const { data: currentUserData } = useCurrentUserQuery();
    const isAdmin =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );
    // Editing notes / expected payment date is for sales users (matching the
    // backend updateOrderSaleDetails gate, RoleId.Sales). Asistente Ventas is
    // read-only, so it's deliberately excluded.
    const isSales =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Sales].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    // Typed sort: the chip list and the query share the generated
    // OrderSalesSortableFields enum — no column-index mapping.
    const { sort, toggle: toggleSort } =
        useCardListSort<OrderSalesSortableFields>(KEY);

    const [orderSaleReceiptTypeId, setOrderSaleReceiptTypeId] = useNumber({
        KEY: KEY + '_OrderSaleReceiptTypeId',
    });
    const [orderSaleStatusId, setOrderSaleStatusId] = useNumber({
        KEY: KEY + '_OrderSaleStatusId',
    });
    const [accountId, setAccountId] = useNumber({ KEY: KEY + '_AccountId' });

    const [noSupplement, setNoSupplement] = useBoolean({
        KEY: KEY + '_NoSupplement',
        defaultValue: false,
    });
    const [noCreditNote, setNoCreditNote] = useBoolean({
        KEY: KEY + '_NoCreditNote',
        defaultValue: false,
    });
    const [isTransferComplete, setIsTransferComplete] = useBoolean({
        KEY: KEY + '_isTransferComplete',
        defaultValue: false,
    });

    const {
        filter,
        setFilter,
        debouncedFilter,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        skip,
        take,
        setSkip,
        selectedId: orderSaleId,
        setSelectedId: setOrderSaleId,
        deleteOpen,
        setDeleteOpen,
        openDelete,
    } = usePaginatedListBlock({
        storageKey: KEY,
        embedded,
        resetDeps: [accountId, orderSaleStatusId, orderSaleReceiptTypeId],
    });

    // Sales has a bespoke upsert dialog ({ open, disabled }), so it sets the
    // selected id + opens that itself rather than using the hook's openEdit.
    const handleEditOrderSale = useCallback(
        (os: SaleDoc) => {
            setOrderSaleId(os.id);
            setUpsertDialogOptions({ open: true, disabled: false });
        },
        [setOrderSaleId],
    );
    const handleDeleteOrderSale = useCallback(
        (os: SaleDoc) => openDelete(os.id),
        [openDelete],
    );
    const handlePrintOrderSale = useCallback(
        (os: SaleDoc) => setSelectedOrderSale?.({ ...os }),
        [setSelectedOrderSale],
    );
    const handleChangeStatus = useCallback(
        (os: SaleDoc) => {
            setOrderSaleId(os.id);
            setStatusId(os.order_sale_status?.id ?? null);
            setStatusOpen(true);
        },
        [setOrderSaleId],
    );
    const handleEditDetails = useCallback(
        (os: SaleDoc) => {
            setOrderSaleId(os.id);
            setDetailsOpen(true);
        },
        [setOrderSaleId],
    );

    useEffect(() => {
        void query({
            variables: {
                skip,
                take,
                startDate: startDate || null,
                endDate: endDate || null,
                filter: debouncedFilter,
                sortField: sort?.field ?? null,
                sortOrder: sort
                    ? sort.order === 'asc'
                        ? ColumnOrder.Asc
                        : ColumnOrder.Desc
                    : null,
                orderSaleReceiptTypeId,
                accountId,
                orderSaleStatusId,
                productId,
                noSupplement,
                noCreditNote,
                isTransferComplete,
            },
        });
    }, [
        query, skip, take, startDate, endDate, debouncedFilter,
        sort, accountId, orderSaleReceiptTypeId,
        orderSaleStatusId, productId, noSupplement, noCreditNote, isTransferComplete,
    ]);

    const { docs: orderSales, count } = selectPaginated(
        data?.paginatedOrderSales,
        previousData?.paginatedOrderSales,
    );

    // Sum each row's product lines once (memoized) for the embedded columns.
    const summaries = useMemo(
        () =>
            embedded
                ? orderSales.map((os) => {
                      const lines = os.order_sale_products.filter(
                          (p) => p.product_id === productId,
                      );
                      return {
                          kilos: lines.reduce((a, p) => a + p.kilos, 0),
                          groups: lines.reduce((a, p) => a + p.groups, 0),
                          total: lines.reduce((a, p) => a + p.total, 0),
                      };
                  })
                : [],
        [orderSales, productId, embedded],
    );

    const folioOf = (os: SaleDoc) =>
        os.invoice_code === 0
            ? String(os.order_code)
            : `${os.order_code} F-${os.invoice_code}`;

    if (scope) {
        return (
            <>
                <CMinifiedTable
                    title={'Ventas'}
                    loading={loading}
                    filterValue={filter}
                    onFilterChange={setFilter}
                    filterHelperText={
                        'Número: folio exacto; texto: cliente, notas, tipo, estatus o producto.'
                    }
                    skip={skip}
                    take={take}
                    setSkip={setSkip}
                    count={count}
                    onRowDoubleClick={(os) => handleEditOrderSale(os)}
                    items={[
                        { title: shortLabel(FIELD_LABELS.date), textAlign: 'center', content: (os) => formatDate(os.date) },
                        { title: shortLabel(COLUMN_LABELS.sale_folio), content: (os) => folioOf(os) },
                        { title: shortLabel(FIELD_LABELS.receipt_type_id), content: (os) => os.receipt_type?.name },
                        {
                            title: shortLabel(COLUMN_LABELS.reconciliation),
                            content: (os) =>
                                os.reconciliation_only
                                    ? 'Solo conciliación'
                                    : 'Operación',
                        },
                        { title: shortLabel(FIELD_LABELS.order_sale_status_id), content: (os) => os.order_sale_status?.name },
                        { title: shortLabel(ORDER_SALE_FIELD_LABELS.account_id), content: (os) => os.account?.name },
                        { title: shortLabel(FIELD_LABELS.groups), textAlign: 'right', content: (_os, i) => formatFloat(summaries[i]?.groups ?? 0) },
                        { title: shortLabel(FIELD_LABELS.kilos), textAlign: 'right', content: (_os, i) => formatFloat(summaries[i]?.kilos ?? 0) },
                        { title: shortLabel(COLUMN_LABELS.row_total), textAlign: 'right', content: (_os, i) => formatFloat(summaries[i]?.total ?? 0) },
                    ]}
                    data={orderSales}
                />
                <SUpsertOrderSale
                    open={open}
                    setOpen={(o) => setUpsertDialogOptions({ open: o, disabled: false })}
                    orderSaleId={orderSaleId}
                    disabled={disabled}
                />
                <SDeleteOrderSaleDialog
                    open={deleteOpen}
                    setOpen={setDeleteOpen}
                    orderSaleId={orderSaleId}
                />
            </>
        );
    }

    return (
        <DBlock>
            <CCardList<SaleDoc, OrderSalesSortableFields>
                title={'Ventas'}
                data={orderSales}
                loading={loading}
                keyOf={(os) => os.id}
                renderItem={(os) => (
                    <SaleCard
                        orderSale={os}
                        onEdit={handleEditOrderSale}
                        onDelete={handleDeleteOrderSale}
                        onPrint={handlePrintOrderSale}
                        onChangeStatus={
                            isAdmin ? handleChangeStatus : undefined
                        }
                        onEditDetails={isSales ? handleEditDetails : undefined}
                    />
                )}
                skip={skip}
                take={take}
                count={count}
                setSkip={setSkip}
                filterValue={filter}
                onFilterChange={setFilter}
                filterHelperText={
                    'Número: folio exacto de pedido, venta o factura; texto: cliente, notas, tipo, estatus o producto.'
                }
                activeFilterCount={
                    (orderSaleReceiptTypeId != null ? 1 : 0) +
                    (startDate && endDate ? 1 : 0) +
                    (orderSaleStatusId != null ? 1 : 0) +
                    (accountId != null ? 1 : 0) +
                    (noSupplement ? 1 : 0) +
                    (noCreditNote ? 1 : 0) +
                    (isTransferComplete ? 1 : 0)
                }
                filters={
                    <>
                        <DIdAutocomplete
                            id={orderSaleReceiptTypeId}
                            onChange={setOrderSaleReceiptTypeId}
                            itemText={'name'}
                            label={'Tipo de recibo'}
                            options={data?.getReceiptTypes || []}
                            disableGutters
                        />
                        <DDateRangePicker
                            label={'Fecha'}
                            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined}
                            onChange={(range: DateRange) => {
                                setStartDate(range[0].format(DATE_FORMAT));
                                setEndDate(range[1].format(DATE_FORMAT));
                            }}
                            onClear={() => { setStartDate(''); setEndDate(''); }}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={orderSaleStatusId}
                            onChange={setOrderSaleStatusId}
                            itemText={'name'}
                            label={'Estatus'}
                            options={data?.getOrderSaleStatuses || []}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={accountId}
                            onChange={setAccountId}
                            itemText={'name'}
                            label={'Cliente'}
                            options={data?.getAccounts || []}
                            disableGutters
                        />
                        <DCheckbox align={'left'} disableGutters onChange={setNoSupplement} value={noSupplement} label={'Sin complemento'} />
                        <DCheckbox align={'left'} disableGutters onChange={setNoCreditNote} value={noCreditNote} label={'Sin nota de crédito'} />
                        <DCheckbox align={'left'} disableGutters onChange={setIsTransferComplete} value={isTransferComplete} label={'Pendientes por transferir'} />
                    </>
                }
                sortOptions={[
                    { label: 'Pedido', field: OrderSalesSortableFields.OrderRequest },
                    { label: 'Folio', field: OrderSalesSortableFields.OrderCode },
                    { label: 'Fecha', field: OrderSalesSortableFields.Date },
                ]}
                sort={sort}
                onSortToggle={toggleSort}
                onItemDoubleClick={handleEditOrderSale}
                contextItems={[
                    {
                        title: shortLabel(COLUMN_LABELS.edit),
                        disable: (orderSale) => !orderSale.is_editable,
                        callback: (orderSale) => handleEditOrderSale(orderSale),
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.delete),
                        disable: (orderSale) => !orderSale.is_deletable,
                        callback: (orderSale) => handleDeleteOrderSale(orderSale),
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.print_pdf),
                        callback: (orderSale) => handlePrintOrderSale(orderSale),
                    },
                    ...(isAdmin
                        ? [
                              {
                                  title: shortLabel(
                                      COLUMN_LABELS.change_status,
                                  ),
                                  callback: (orderSale: SaleDoc) =>
                                      handleChangeStatus(orderSale),
                              },
                          ]
                        : []),
                    ...(isSales
                        ? [
                              {
                                  title: shortLabel(
                                      COLUMN_LABELS.edit_optional_details,
                                  ),
                                  callback: (orderSale: SaleDoc) =>
                                      handleEditDetails(orderSale),
                              },
                          ]
                        : []),
                ]}
            />
            <SUpsertOrderSale
                open={open}
                setOpen={(o) => setUpsertDialogOptions({ open: o, disabled: false })}
                orderSaleId={orderSaleId}
                disabled={disabled}
            />
            <SDeleteOrderSaleDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                orderSaleId={orderSaleId}
            />
            <SUpdateOrderSaleStatusDialog
                open={statusOpen}
                setOpen={setStatusOpen}
                orderSaleId={orderSaleId}
                currentStatusId={statusId}
            />
            <SUpdateOrderSaleDetailsDialog
                open={detailsOpen}
                setOpen={setDetailsOpen}
                orderSaleId={orderSaleId}
            />
        </DBlock>
    );
}
